import { Mic, Play, RotateCcw, Square } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FeedbackAudioKey } from '../hooks/useFeedbackAssistant';

export type RecorderState =
  'idle' | 'instructions' | 'ready' | 'armed' | 'recording' | 'paused' | 'saved';

const MAX_DURATION_SECONDS = 90;
const PAUSE_AT_SECONDS = 80;
const VOICE_THRESHOLD = 0.02;
const SILENCE_TIMEOUT_MS = 10_000;

interface VoiceRecorderProps {
  onClipRecorded: (blob: Blob, durationMs: number) => void;
  onMicUnavailable?: () => void;
  playAudio?: (key: FeedbackAudioKey) => Promise<void>;
  enabled?: boolean;
}

export function VoiceRecorder({
  onClipRecorded,
  onMicUnavailable,
  playAudio,
  enabled = true,
}: VoiceRecorderProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [hasMic, setHasMic] = useState(true);

  const stateRef = useRef<RecorderState>('idle');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const elapsedTimeRef = useRef(0);
  const pausedAtRef = useRef(0);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasVoiceRef = useRef(false);
  const saveOnceRef = useRef(false);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const resetSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const startSilenceTimer = useCallback(() => {
    resetSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      forceStop();
    }, SILENCE_TIMEOUT_MS);
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => {
      elapsedTimeRef.current += 1;
      setElapsedTime(elapsedTimeRef.current);
      if (elapsedTimeRef.current >= PAUSE_AT_SECONDS) pauseRecording();
    }, 1000);
  }, []);

  const forceStop = useCallback(() => {
    resetSilenceTimer();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    saveOnceRef.current = false;
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      /* ignore */
    }
    stopTimer();
    chunksRef.current = [];
    streamRef.current?.getTracks().forEach(t => t.stop());
    audioContextRef.current?.close();
    elapsedTimeRef.current = 0;
    pausedAtRef.current = 0;
    hasVoiceRef.current = false;
    setElapsedTime(0);
    setAudioLevel(0);
    setState('idle');
  }, []);

  const stopAndSave = useCallback(() => {
    resetSilenceTimer();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      /* ignore */
    }
    stopTimer();
    setState('saved');
  }, []);

  // VAD loop via RAF — reads state from ref
  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;
    const s = stateRef.current;
    if (s !== 'armed' && s !== 'recording') return;

    const dataArray = new Uint8Array(analyserRef.current.fftSize);
    analyserRef.current.getByteTimeDomainData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const val = (dataArray[i] - 128) / 128;
      sum += val * val;
    }
    const rms = Math.sqrt(sum / dataArray.length);
    setAudioLevel(Math.min(rms * 3, 1));

    if (s === 'armed' && rms > VOICE_THRESHOLD && !hasVoiceRef.current) {
      hasVoiceRef.current = true;
      setState('recording');
      elapsedTimeRef.current = pausedAtRef.current;
      setElapsedTime(pausedAtRef.current);
      startTimer();
      startSilenceTimer();
    }

    if (s === 'recording' && rms > VOICE_THRESHOLD) {
      startSilenceTimer();
    }

    rafRef.current = requestAnimationFrame(updateAudioLevel);
  }, [startTimer, startSilenceTimer]);

  useEffect(() => {
    if (!('mediaDevices' in navigator)) {
      setHasMic(false);
      return;
    }
    navigator.mediaDevices
      .enumerateDevices()
      .then(d => {
        if (!d.some(x => x.kind === 'audioinput')) setHasMic(false);
      })
      .catch(() => setHasMic(false));
  }, []);

  const initMediaRecorder = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      saveOnceRef.current = false;

      mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = () => {
        if (saveOnceRef.current) return;
        saveOnceRef.current = true;
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const durationMs = elapsedTimeRef.current * 1000;
        stream.getTracks().forEach(t => t.stop());
        audioContext.close();
        if (blob.size > 1000 && hasVoiceRef.current) onClipRecorded(blob, durationMs);
      };

      return true;
    } catch {
      setHasMic(false);
      onMicUnavailable?.();
      playAudio?.('mic_unavailable');
      return false;
    }
  };

  // Step 1: play record_rules on mount, then show button
  // Ready immediately — parent plays category_confirm then record_rules before showing this
  const hasInitRef = useRef(false);
  useEffect(() => {
    if (hasInitRef.current || !enabled || !hasMic) return;
    hasInitRef.current = true;
    setState('ready');
  }, [enabled, hasMic]);

  // Step 2: user taps green button → play recording_active → init mic → arm
  const handleStartRecording = async () => {
    setState('armed'); // show "armed" UI while instructions play

    // Play "Parlez maintenant..."
    await playAudio?.('recording_active');

    // Init mic
    const ok = await initMediaRecorder();
    if (!ok) {
      setState('idle');
      return;
    }

    if (mediaRecorderRef.current?.state === 'inactive') {
      mediaRecorderRef.current.start();
    }

    hasVoiceRef.current = false;
    rafRef.current = requestAnimationFrame(updateAudioLevel);
  };

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      stopTimer();
      resetSilenceTimer();
      setState('paused');
      playAudio?.('recording_ending');
    }
  }, [playAudio]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setState('recording');
      startTimer();
      startSilenceTimer();
      rafRef.current = requestAnimationFrame(updateAudioLevel);
    }
  }, [startTimer, startSilenceTimer, updateAudioLevel]);

  useEffect(() => {
    return () => {
      stopTimer();
      resetSilenceTimer();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      audioContextRef.current?.close();
    };
  }, []);

  if (!enabled || !hasMic) return null;

  return (
    <div className="flex flex-col items-center gap-4">
      <div
        className={`flex h-20 w-20 items-center justify-center rounded-full ${
          state === 'recording'
            ? 'bg-[#f9594c]/10'
            : state === 'paused'
              ? 'bg-amber-100'
              : state === 'armed'
                ? 'bg-primary/20 animate-pulse'
                : 'bg-primary/10'
        }`}
      >
        <Mic
          className={`h-8 w-8 ${
            state === 'recording'
              ? 'text-[#f9594c] animate-pulse'
              : state === 'paused'
                ? 'text-amber-600'
                : 'text-primary'
          }`}
        />
      </div>

      <div className="flex items-center gap-1" aria-hidden="true">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className={`w-1 rounded-full transition-all duration-75 ${
              state === 'recording' ? 'bg-[#f9594c]' : 'bg-primary'
            }`}
            style={{
              height: `${state === 'recording' || state === 'armed' ? Math.max(4, audioLevel * 32 * Math.sin((i / 20) * Math.PI)) : 4}px`,
            }}
          />
        ))}
      </div>

      <p className="text-sm text-muted-foreground" aria-live="polite">
        {state === 'instructions'
          ? t('Feedback.recordingState.ready')
          : state === 'armed'
            ? t('Feedback.recordingState.armed')
            : state === 'recording'
              ? `${formatTime(elapsedTime)} / ${formatTime(MAX_DURATION_SECONDS)}`
              : state === 'paused'
                ? `${formatTime(elapsedTime)} / ${formatTime(MAX_DURATION_SECONDS)} — ${t('Feedback.recordingState.resume')}`
                : t('Feedback.detail.speak')}
      </p>

      <div className="flex items-center gap-3">
        {/* Green button: disabled during instructions, active when ready */}
        {(state === 'instructions' || state === 'ready') && (
          <button
            type="button"
            onClick={handleStartRecording}
            disabled={state === 'instructions'}
            className={`flex items-center gap-2 rounded-full px-5 py-3 shadow-lg transition-all ${
              state === 'ready'
                ? 'bg-primary text-primary-foreground hover:scale-105 active:scale-95'
                : 'bg-primary/30 text-primary/50 cursor-not-allowed'
            }`}
          >
            <Mic className="h-4 w-4" />
            <span className="text-sm font-medium">{t('Feedback.detail.speak')}</span>
          </button>
        )}

        {state === 'armed' && (
          <div className="flex items-center gap-2 rounded-full bg-primary/20 px-5 py-3 text-primary animate-pulse">
            <Mic className="h-4 w-4 animate-pulse" />
            <span className="text-sm font-medium">{t('Feedback.recordingState.armed')}</span>
          </div>
        )}

        {state === 'recording' && (
          <button
            type="button"
            onClick={stopAndSave}
            className="flex items-center gap-2 rounded-full bg-[#f9594c] px-5 py-3 text-white shadow-lg"
          >
            <Square className="h-4 w-4 fill-current" />
            <span className="text-sm font-medium">{t('Feedback.recordingState.finish')}</span>
          </button>
        )}

        {state === 'paused' && (
          <>
            <button
              type="button"
              onClick={resumeRecording}
              className="flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-primary-foreground shadow-lg"
            >
              <Play className="h-4 w-4" />
              <span className="text-sm font-medium">{t('Feedback.recordingState.resume')}</span>
            </button>
            <button
              type="button"
              onClick={stopAndSave}
              className="flex items-center gap-2 rounded-full bg-[#f9594c] px-5 py-3 text-white shadow-lg"
            >
              <Square className="h-4 w-4 fill-current" />
              <span className="text-sm font-medium">{t('Feedback.recordingState.finish')}</span>
            </button>
          </>
        )}

        {state === 'saved' && (
          <button
            type="button"
            onClick={() => {
              // Re-init: play instructions, then arm mic again
              hasInitRef.current = false;
              setState('instructions');
              playAudio?.('record_rules').finally(() => {
                if (stateRef.current === 'instructions') {
                  setState('ready');
                }
              });
            }}
            className="flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-medium hover:bg-muted"
          >
            <RotateCcw className="h-4 w-4" />
            <span>{t('Feedback.clipList.addMore')}</span>
          </button>
        )}
      </div>
    </div>
  );
}
